import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Store, Search, MapPin, AlertTriangle, Loader2 } from 'lucide-react'
import { cn } from 'lib/utils'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { Checkbox } from 'components/ui/checkbox'
import { Button } from 'components/ui/button'
import { TRADE_AREA_RINGS } from 'features/Map'
import type { OtherRetailCategory } from './OtherRetailPanel'

/**
 * Scan for nearby retail, pick what is worth keeping.
 *
 * Linear: MYD-484.
 *
 * Conforms to the popup shape set by `EvaluationSiteForm`: a 40px tinted icon
 * tile in the header, a `p-5` body, and the actions centred at the foot of the
 * body rather than in a `DialogFooter`.
 *
 * Note `components/ui/dialog` is hand-rolled, not Radix: the prop is `onClose`,
 * not `onOpenChange`, and `DialogContent` renders its own close button.
 */

const CATEGORY_CHOICES: { value: OtherRetailCategory; label: string }[] = [
	{ value: 'poi', label: 'Retail POI' },
	{ value: 'competitor', label: 'Competitor' },
	{ value: 'cotenant', label: 'Cotenant' },
]

const DEFAULT_RADIUS = Math.max(...TRADE_AREA_RINGS)

interface OtherRetailScanDialogProps {
	open: boolean
	onClose: () => void
	anchorName?: string | null
	onScan: (options: {
		radiusMiles: number
		categories?: string[]
		namedChainsOnly: boolean
	}) => void
	onSave: (items: any[]) => void
}

/** Logo when there is one, a coloured monogram chip when there is not. */
function BrandTile({ item, size = 32 }: { item: any; size?: number }) {
	const logo = item.brand?.logoUrl
	const color = item.brand?.monogramColor || '#4A5568'
	const monogram = item.brand?.monogram || (item.name || '?').charAt(0).toUpperCase()

	return (
		<div
			className="flex shrink-0 items-center justify-center overflow-hidden rounded-lg"
			style={{
				width: size,
				height: size,
				background: logo ? '#ffffff' : color,
				border: logo ? '1px solid #e5e7eb' : 'none',
			}}
		>
			{logo ? (
				<img src={logo} alt="" loading="lazy" className="h-full w-full object-contain p-0.5" />
			) : (
				<span className="text-xs font-semibold text-white">{monogram}</span>
			)}
		</div>
	)
}

export function OtherRetailScanDialog({
	open,
	onClose,
	anchorName,
	onScan,
	onSave,
}: OtherRetailScanDialogProps) {
	const { scan, isSaving } = useSelector((state: any) => state.otherRetailers)

	const [radiusMiles, setRadiusMiles] = useState(DEFAULT_RADIUS)
	const [namedChainsOnly, setNamedChainsOnly] = useState(true)
	const [search, setSearch] = useState('')
	const [activeCategories, setActiveCategories] = useState<string[]>([])
	const [selectedIds, setSelectedIds] = useState<string[]>([])
	const [tag, setTag] = useState<OtherRetailCategory>('poi')

	// Fire the first scan when the dialog opens, and reset the per-run state so
	// reopening never shows the previous run's selection.
	useEffect(() => {
		if (!open) return
		setSelectedIds([])
		setSearch('')
		setActiveCategories([])
		onScan({ radiusMiles: DEFAULT_RADIUS, namedChainsOnly: true })
		// Intentionally keyed on `open` alone: re-running whenever `onScan`
		// changes identity would scan on every parent render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	const results: any[] = scan.results || []

	const filtered = useMemo(() => {
		const needle = search.trim().toLowerCase()
		return results.filter((r) => {
			if (activeCategories.length && !activeCategories.includes(r.retailCategory)) return false
			if (!needle) return true
			return (
				(r.name || '').toLowerCase().includes(needle) ||
				(r.brandName || '').toLowerCase().includes(needle) ||
				(r.address || '').toLowerCase().includes(needle)
			)
		})
	}, [results, search, activeCategories])

	/** Grouped for scanning by eye: a retail list is read by category, not alphabetically. */
	const grouped = useMemo(() => {
		const groups = new Map<string, any[]>()
		for (const row of filtered) {
			const key = row.retailCategory || 'other'
			if (!groups.has(key)) groups.set(key, [])
			groups.get(key)!.push(row)
		}
		return [...groups.entries()]
	}, [filtered])

	const labelFor = (key: string) =>
		scan.categories?.find((c: any) => c.key === key)?.label || 'Other Retail'

	const selectable = filtered.filter((r) => !r.alreadySaved)
	const selectedSet = new Set(selectedIds)

	const toggle = (id: string) =>
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

	const rerun = (next: { radiusMiles?: number; namedChainsOnly?: boolean }) => {
		const radius = next.radiusMiles ?? radiusMiles
		const chains = next.namedChainsOnly ?? namedChainsOnly
		setRadiusMiles(radius)
		setNamedChainsOnly(chains)
		setSelectedIds([])
		onScan({ radiusMiles: radius, namedChainsOnly: chains })
	}

	const handleSave = () => {
		const chosen = results
			.filter((r) => selectedSet.has(r.sourceId))
			.map((r) => ({ ...r, category: tag }))
		onSave(chosen)
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="flex max-h-[85vh] max-w-3xl flex-col">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0080fe]/10 text-[#0080fe]">
							<Store className="h-5 w-5" />
						</div>
						<div>
							<DialogTitle>Scan for Other Retailers</DialogTitle>
							<DialogDescription>
								Nearby retail within {radiusMiles} mile{radiusMiles === 1 ? '' : 's'}
								{anchorName ? ` of ${anchorName}` : ''}. Pick what is worth keeping.
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="flex min-h-0 flex-1 flex-col gap-3 p-5">
					{/* Controls */}
					<div className="flex flex-wrap items-center gap-2">
						<div className="relative flex-1 min-w-[180px]">
							<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
							<input
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Search name or address"
								className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-3 text-sm focus:border-[#0080fe] focus:outline-none"
							/>
						</div>

						<select
							value={radiusMiles}
							onChange={(e) => rerun({ radiusMiles: Number(e.target.value) })}
							className="rounded-md border border-gray-200 px-2 py-1.5 text-sm focus:border-[#0080fe] focus:outline-none"
						>
							{TRADE_AREA_RINGS.map((r) => (
								<option key={r} value={r}>
									{r} mile{r === 1 ? '' : 's'}
								</option>
							))}
						</select>

						{/* Default ON. Without it a 5-mile scan is hundreds of rows of
						    independents and the list stops being usable. */}
						<label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
							<Checkbox
								checked={namedChainsOnly}
								onCheckedChange={(next) => rerun({ namedChainsOnly: next })}
							/>
							Named chains only
						</label>
					</div>

					{/* Category chips, server-driven so they always describe the
					    taxonomy the scan actually used. */}
					{scan.categories?.length > 0 && (
						<div className="flex flex-wrap gap-1.5">
							{scan.categories.map((c: any) => {
								const active = activeCategories.includes(c.key)
								return (
									<button
										key={c.key}
										type="button"
										onClick={() =>
											setActiveCategories((prev) =>
												prev.includes(c.key)
													? prev.filter((x) => x !== c.key)
													: [...prev, c.key],
											)
										}
										className={cn(
											'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
											active
												? 'border-[#0080fe] bg-[#0080fe] text-white'
												: 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
										)}
									>
										{c.label} <span className="tabular-nums opacity-70">{c.count}</span>
									</button>
								)
							})}
						</div>
					)}

					{/* States */}
					{scan.isScanning && (
						<div className="flex flex-1 items-center justify-center gap-2 text-sm text-gray-500">
							<Loader2 className="h-4 w-4 animate-spin" />
							Scanning nearby retail...
						</div>
					)}

					{!scan.isScanning && !scan.providerOk && (
						<div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
							<AlertTriangle className="h-6 w-6 text-amber-500" />
							<p className="m-0 text-sm font-medium text-gray-800">
								The POI service did not respond
							</p>
							{/* Worded apart from "none found" deliberately: the two mean
							    very different things to someone judging a trade area. */}
							<p className="m-0 max-w-sm text-xs text-gray-500">
								This is usually the public OpenStreetMap service being busy rather than
								anything wrong with this location. Try again in a moment.
							</p>
							<Button variant="ghost" className="mt-1" onClick={() => rerun({})}>
								Retry
							</Button>
						</div>
					)}

					{!scan.isScanning && scan.providerOk && results.length === 0 && (
						<div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-gray-500">
							No {namedChainsOnly ? 'named chains' : 'retail'} found within {radiusMiles} mile
							{radiusMiles === 1 ? '' : 's'}.
							{namedChainsOnly && ' Turn off "Named chains only" to include independents.'}
						</div>
					)}

					{!scan.isScanning && scan.providerOk && results.length > 0 && (
						<>
							<div className="flex items-center justify-between text-xs text-gray-500">
								<span>
									{filtered.length} of {results.length} shown
									{scan.cached && ' · cached'}
								</span>
								<span className="flex gap-2">
									<button
										type="button"
										className="hover:text-[#0080fe]"
										onClick={() => setSelectedIds(selectable.map((r) => r.sourceId))}
									>
										Select all
									</button>
									<button
										type="button"
										className="hover:text-[#0080fe]"
										onClick={() => setSelectedIds([])}
									>
										None
									</button>
								</span>
							</div>

							<div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-gray-200">
								{grouped.map(([categoryKey, rows]) => (
									<div key={categoryKey}>
										<div className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
											{labelFor(categoryKey)} · {rows.length}
										</div>
										{rows.map((row) => {
											const checked = row.alreadySaved || selectedSet.has(row.sourceId)
											return (
												<label
													key={row.sourceId}
													className={cn(
														'flex items-center gap-3 border-b border-gray-100 px-3 py-2 transition-colors',
														row.alreadySaved
															? 'cursor-default bg-gray-50/60'
															: 'cursor-pointer hover:bg-blue-50/40',
													)}
													onClick={(e) => {
														// The whole row is the target, not just the 16px
														// box. `Checkbox` is a div rather than a real
														// input, so a label does not toggle it for us;
														// this stands in for that. Clicks that came from
														// the checkbox are skipped so the two handlers
														// do not cancel each other out.
														if (row.alreadySaved) return
														if ((e.target as HTMLElement).closest('[role="checkbox"]')) return
														toggle(row.sourceId)
													}}
												>
													<Checkbox
														checked={checked}
														disabled={row.alreadySaved}
														onCheckedChange={() => toggle(row.sourceId)}
													/>
													<BrandTile item={row} />
													<div className="min-w-0 flex-1">
														<div className="truncate text-sm font-medium text-gray-900">
															{row.name}
														</div>
														{row.address && (
															<div className="truncate text-xs text-gray-500">
																{row.address}
															</div>
														)}
													</div>
													{row.distanceMiles != null && (
														<span className="flex shrink-0 items-center gap-0.5 text-xs tabular-nums text-gray-400">
															<MapPin className="h-3 w-3" />
															{row.distanceMiles.toFixed(1)} mi
														</span>
													)}
													{row.alreadySaved && (
														<span className="shrink-0 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success">
															Saved
														</span>
													)}
												</label>
											)
										})}
									</div>
								))}
							</div>

							{/* Actions centred at the foot of the body, per the
							    conformed popup shape. */}
							<div className="flex flex-col items-center gap-2 pt-1">
								<div className="flex items-center gap-2">
									<span className="text-xs text-gray-500">Add as</span>
									<select
										value={tag}
										onChange={(e) => setTag(e.target.value as OtherRetailCategory)}
										className="rounded-md border border-gray-200 px-2 py-1.5 text-sm focus:border-[#0080fe] focus:outline-none"
									>
										{CATEGORY_CHOICES.map((c) => (
											<option key={c.value} value={c.value}>
												{c.label}
											</option>
										))}
									</select>
									<Button
										onClick={handleSave}
										disabled={selectedIds.length === 0 || isSaving}
									>
										{isSaving
											? 'Saving...'
											: `Add ${selectedIds.length || ''} retailer${
													selectedIds.length === 1 ? '' : 's'
												}`.replace('  ', ' ')}
									</Button>
								</div>
								<p className="m-0 text-xs italic text-[#0080fe]">
									Tag competitors and cotenants now, so the list is worth reading later.
								</p>
							</div>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default OtherRetailScanDialog
